import React, { useEffect, useState } from "react";
import emailjs from "emailjs-com";
import { X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const LeadPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  useEffect(() => {
    const hasSubmitted = localStorage.getItem("ies_lead_popup_submitted");

    if (!hasSubmitted) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 2500);

      return () => clearTimeout(timer);
    }
  }, []);

  const closePopup = () => setIsOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    emailjs
      .send(
        "service_1nktqrk",
        "template_kkktiwj",
        {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message:
            formData.message ||
            "Customer claimed 20% discount on first bulk order.",
        },
        "FdSRX__UgVs90SMPn"
      )
      .then(
        () => {
          localStorage.setItem("ies_lead_popup_submitted", "true");
          setSubmitted(true);
          setIsSubmitting(false);

          setFormData({
            name: "",
            email: "",
            phone: "",
            message: "",
          });

          setTimeout(() => {
            setIsOpen(false);
          }, 2200);
        },
        () => {
          setIsSubmitting(false);
          alert("Failed to send. Please try again.");
        }
      );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 md:p-6"
          initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
          animate={{ opacity: 1, backdropFilter: "blur(8px)" }}
          exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 24 }}
            transition={{
              duration: 0.45,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="relative w-full max-w-6xl bg-white rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl max-h-[95vh] overflow-y-auto"
          >
            {/* Close Button */}
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 md:top-5 md:right-5 z-30 text-white md:text-black hover:opacity-70 transition"
            >
              <X size={24} className="md:w-[26px] md:h-[26px]" />
            </button>

            <div className="grid grid-cols-1 md:grid-cols-2 min-h-auto md:min-h-[620px]">
              {/* Left Side */}
              <div className="bg-black text-white px-6 py-10 sm:px-8 sm:py-12 md:p-14 flex flex-col justify-center relative overflow-hidden">
                <img
                  src="/logo512.png"
                  alt="IES Gear Logo"
                  className="absolute inset-0 w-full h-full object-contain opacity-10 blur-[1px] p-8 md:p-10 pointer-events-none"
                />

                <div className="relative z-10">
                  <p className="uppercase tracking-[0.25em] md:tracking-[0.3em] text-[11px] sm:text-xs md:text-sm text-gray-400 mb-3 md:mb-4">
                    Exclusive First Order Offer
                  </p>

                  <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold leading-tight mb-4">
                    Get <span className="text-[#FF0000]">20% OFF</span>
                  </h2>

                  <p className="text-sm sm:text-base md:text-lg text-gray-300 mb-5 md:mb-6 leading-relaxed max-w-xl">
                    Unlock your exclusive discount on your first bulk order of
                    premium custom combat sports gear.
                  </p>

                  <div className="space-y-2.5 md:space-y-3 text-xs sm:text-sm text-gray-400">
                    <p>✔ Premium Custom Manufacturing</p>
                    <p>✔ Low MOQ Available</p>
                    <p>✔ Private Label & OEM Services</p>
                    <p>✔ Fast Worldwide Delivery</p>
                  </div>
                </div>
              </div>

              {/* Right Side */}
              <div className="p-5 sm:p-7 md:p-12 flex items-center">
                {!submitted ? (
                  <div className="w-full">
                    <div className="mb-5 md:mb-6">
                      <h3 className="text-2xl sm:text-3xl font-bold text-black mb-2">
                        Claim Your Discount
                      </h3>
                      <p className="text-gray-600 text-sm sm:text-[15px]">
                        Fill in your details and receive 20% OFF on your first
                        order.
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-3.5 md:space-y-4">
                      <div>
                        <label className="block text-black font-medium mb-1.5 text-sm">
                          Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                        />
                      </div>

                      <div>
                        <label className="block text-black font-medium mb-1.5 text-sm">
                          Email
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                        />
                      </div>

                      <div>
                        <label className="block text-black font-medium mb-1.5 text-sm">
                          Phone
                        </label>
                        <input
                          type="text"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                        />
                      </div>

                      <div>
                        <label className="block text-black font-medium mb-1.5 text-sm">
                          Message
                        </label>
                        <textarea
                          name="message"
                          rows="4"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us what products you're looking for..."
                          className="w-full px-4 py-3 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-black text-white font-bold border-2 border-black py-3.5 px-4 rounded-xl hover:bg-white hover:text-black duration-200 text-sm sm:text-base"
                      >
                        {isSubmitting
                          ? "Processing..."
                          : "Unlock My 20% Discount"}
                      </button>
                    </form>
                  </div>
                ) : (
                  <div className="w-full text-center py-8">
                    <h3 className="text-3xl sm:text-4xl font-bold text-black mb-4">
                      Thank You!
                    </h3>
                    <p className="text-gray-600 text-base sm:text-lg">
                      Your 20% discount has been unlocked.
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Our team will contact you shortly.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LeadPopup;